import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import SearchBarItem from '../../SearchBarItem';
import OptionList from './OptionList';
import Option from './Option';
import SearchBox from './SearchBox';
import keyCode from 'Constants/keyCodes';
import { destinationOptionsShape } from 'Constants/PropTypes';
import { DEFAULT_FALLBACK_LANGUAGE } from 'Constants/App';

/**
 *"Cache like", intended to serve faster results. It could be improved by not repeating found values.
   E.g. if "a" is typed, it is stored as a: [austria, argentina, ...] and if then "r" is typed
   the cache would be {a: [austria, argentina, ...], ar: [argentina, ...]}. To avoid consuming too much
   memor, a limit of 10 keys is used.
 */

const resultsCache = {};

const CACHE_LENGTH = 10;


const SelectWithSearch = (props) => {

    const {
        options, dispatchSearch,
        hasCategory, noResultsLabel,
        defaultSelected, optionsWrapperClass, showAsDropdown,
        title, className, language,
    } = props;

    const [ list, setList ] = useState(options);
    const [ openList, setOpen ] = useState(false);
    const [ searchInputValue, setSearchInputValue ] = useState('');
    const [ suggestedOption, setSuggestedOption ] = useState('');
    const [ focusedOption, setFocusedOption ] = useState(null);
    const [ displaySelected, setDisplaySelected ] = useState('');
    const searchInputRef = useRef(null);
    const listOptionsRef = useRef(null);

    const searchArray = useMemo(() => {

        const placesArray = options.length ? options.slice(0, -1) : [];

        const hotelsArray =  options.length ? options[options.length - 1].children : [];

        return placesArray.concat(hotelsArray);

    }, [ options ]);

    const cacheSize  =   Object.keys(resultsCache).length;

    const searchEngine = (searchValue) => {
        if (searchValue in resultsCache) {
            setSuggestedOption(resultsCache[searchValue][0]);
            return resultsCache[searchValue];
        }

        const regEx = new RegExp(searchValue, 'ig');
        const newList = searchArray.filter(element => regEx.test(element.label))
            .map((element) => ({
                label        : element.label,
                value        : element.value,
                englishValue : element.englishValue || undefined,
            }));

        setSuggestedOption(newList[0]);

        if (cacheSize + 1 <= CACHE_LENGTH) {
            resultsCache[searchValue] = newList;
        }

        return newList;

    };

    const handleSearch = ({ target: { value } }) => {
        setSearchInputValue(value);
        setList(value ? searchEngine(value) : options);
    };

    const showList = () => {
        setOpen(true);
        if (showAsDropdown) {
            handleSearch({ target: { value: displaySelected } });
        }
    };

    const closeList = () => {
        setOpen(false);
        searchInputRef.current.blur();

        setSearchInputValue(displaySelected);
    };

    const handleDispatch = async (item) => {
        if (item.label && item.label !== 'Hotels') {
            closeList();
            if (displaySelected === item.label) {
                return;
            }
            setDisplaySelected(item.label);
            try {
                await dispatchSearch(item);
            } catch (error) {
                setDisplaySelected('');
                /* eslint-disable-next-line */
                console.error(error);

            }
        }

        setOpen(false);
    };

    const handleListEvents = useCallback(
        (e) => {
            const keysDictionary = {
                [keyCode.tab]    : () => {},
                [keyCode.shift]  : () => {},
                [keyCode.enter]  : () => handleDispatch(focusedOption || suggestedOption),
                [keyCode.escape] : () => closeList(),
            };
            // handle keyPress
            if (keysDictionary[e.keyCode]) {
                return keysDictionary[e.keyCode]();
            }
            // handle click
            const isComponentClicked = listOptionsRef.current.contains(e.target) || searchInputRef.current.contains(e.target);
            return isComponentClicked ? searchInputRef.current.focus() : closeList();
        },
        [ openList, suggestedOption, focusedOption ],
    );

    useEffect(() => {
        if (defaultSelected) {
            setDisplaySelected(defaultSelected);
        }
    }, [ defaultSelected ]);
    useEffect(() => {
        setSearchInputValue(displaySelected);
    }, [ displaySelected ]);
    useEffect(() => {
        if (openList) {
            document.addEventListener('mousedown', handleListEvents);
            document.addEventListener('keydown', handleListEvents);
        } else {
            document.removeEventListener('mousedown', handleListEvents);
            document.removeEventListener('keydown', handleListEvents);
        }
        return () => {
            document.removeEventListener('mousedown', handleListEvents);
            document.removeEventListener('keydown', handleListEvents);
        };
    }, [ openList, suggestedOption, focusedOption ]);

    const isHighlighted = label => label === suggestedOption?.label && !focusedOption;

    const handleInputClick = () => searchInputRef.current.select();

    const handleOptionFocus = item => setFocusedOption(item);

    const handleOptionClick = item => handleDispatch(item);

    const handleOptionOnBlur = () => setFocusedOption(null);

    const showOptionsList = !showAsDropdown || (showAsDropdown && openList);

    return (
        <SearchBarItem
            className={`kbe-select-with-search-main-wrapper ${className}`}
            label={title}
            hasInput
            onClick={handleInputClick}
        >
            <SearchBox
                searchValue={searchInputValue}
                onSearch={handleSearch}
                onClick={handleInputClick}
                reference={searchInputRef}
                onFocus={showList}
                boldText={!openList}
                language={language}
            />
            <OptionList
                show={showOptionsList}
                showAsDropdown={showAsDropdown}
                optionsWrapperClass={optionsWrapperClass}
                reference={listOptionsRef}

            >
                { list && list.length > 0
                    ? list.map((option, index) => {
                        const item = option.item || option;
                        const key = `ITEM_${index}`;
                        return (
                            <div className="kbe-options-list-category-wrapper" key={key}>
                                <Option
                                    isCategory={hasCategory}
                                    label={item.label}
                                    highlighted={isHighlighted(item.label)}
                                    onFocus={() => handleOptionFocus(item)}
                                    onClick={() => handleOptionClick(item)}
                                    onBlur={handleOptionOnBlur}
                                />
                                { hasCategory && item.children ? item.children.map((child, indexChild) => {
                                    const keyChild = `${key}_CHILD_${indexChild}`;
                                    return (
                                        <Option
                                            label={child.label}
                                            key={keyChild}
                                            highlighted={index === 0 && isHighlighted(child.label)}
                                            onFocus={() => handleOptionFocus(child)}
                                            onClick={() => handleOptionClick(child)}
                                            onBlur={handleOptionOnBlur}

                                        />
                                    );
                                }) : null}
                            </div>
                        );
                    })
                    : <Option label={noResultsLabel} />}
            </OptionList>
        </SearchBarItem>
    );
};

SelectWithSearch.defaultProps = {
    options        : [],
    hasCategory    : true,
    dispatchSearch : () => {},
    noResultsLabel      : 'No results found',
    defaultSelected     : '',
    optionsWrapperClass : '',
    showAsDropdown      : true,
    title               : '',
    className           : '',
    language            : DEFAULT_FALLBACK_LANGUAGE,
};

SelectWithSearch.propTypes = {
    options              : PropTypes.arrayOf(PropTypes.shape(destinationOptionsShape)),
    hasCategory          : PropTypes.bool,
    dispatchSearch       : PropTypes.func,
    noResultsLabel       : PropTypes.string,
    defaultSelected      : PropTypes.string,
    optionsWrapperClass  : PropTypes.string,
    showAsDropdown       : PropTypes.bool,
    title                : PropTypes.oneOfType([ PropTypes.string, PropTypes.node ]),
    className            : PropTypes.string,
    language             : PropTypes.string,
};

export default SelectWithSearch;
